import { useMemo, useState } from 'react';
import type { PreviewKind, Resource, ResourceType } from './types';
import { typeStyles } from './constants';
import { getPreviewKind } from './utils';

type ResourcesTableProps = {
  data: {
    resources: Resource[];
    isLoading: boolean;
  };
  actions: {
    onAddResource: () => void;
    onOpenPreview: (resource: Resource, kind: PreviewKind) => void;
  };
};

const ResourceIcon = ({ type }: { type: ResourceType }) => {
  if (type === 'Link') {
    return (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M10 13a5 5 0 0 1 0-7l2-2a5 5 0 0 1 7 7l-1 1" />
        <path d="M14 11a5 5 0 0 1 0 7l-2 2a5 5 0 0 1-7-7l1-1" />
      </svg>
    );
  }

  if (type === 'Image') {
    return (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="m21 15-5-5L5 21" />
      </svg>
    );
  }

  // PDF icon
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
};

const ResourcesTable = ({ data, actions }: ResourcesTableProps) => {
  const [search, setSearch] = useState('');

  const filteredResources = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return data.resources;
    }
    return data.resources.filter((resource) => {
      const shared = resource.sharedWith.join(' ').toLowerCase();
      return (
        resource.name.toLowerCase().includes(query) ||
        resource.type.toLowerCase().includes(query) ||
        shared.includes(query)
      );
    });
  }, [data.resources, search]);

  const handleResourceOpen = (resource: Resource) => {
    if (!resource.link) {
      return;
    }
    const kind = getPreviewKind(resource);
    if (kind === 'none') {
      window.open(resource.link, '_blank', 'noopener,noreferrer');
      return;
    }
    actions.onOpenPreview(resource, kind);
  };

  return (
    <section className="flex min-w-0 flex-1 flex-col gap-6 rounded-[16px] border border-slate-100 bg-white px-5 py-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex w-[260px] items-center gap-2 rounded-[10px] border border-slate-100 bg-slate-50 px-3 py-2 text-slate-400">
            <svg
              className="h-4 w-4 text-slate-400"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-3.5-3.5" />
            </svg>
            <input
              className="w-full bg-transparent text-xs font-semibold text-slate-600 outline-none placeholder:text-slate-400"
              placeholder="Search resources"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>
        <button
          type="button"
          className="flex items-center gap-2 rounded-[10px] bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
          onClick={actions.onAddResource}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3v12" />
            <path d="M6 9h12" />
            <path d="M5 21h14a2 2 0 0 0 2-2V7" />
          </svg>
          Add Resource
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-100">
        <div className="grid grid-cols-[minmax(240px,_2fr)_120px_minmax(180px,_1.5fr)_120px] gap-4 border-b border-slate-100 bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          <span>Resource Name</span>
          <span>Type</span>
          <span>Shared With</span>
          <span>Added On</span>
        </div>
        <div className="divide-y divide-slate-100">
          {data.isLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <div
                key={`resource-skeleton-${index}`}
                className="grid grid-cols-[minmax(240px,_2fr)_120px_minmax(180px,_1.5fr)_120px] items-center gap-4 px-4 py-4"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-slate-100" />
                  <div className="space-y-2">
                    <div className="h-3 w-40 rounded bg-slate-100" />
                    <div className="h-2 w-56 rounded bg-slate-50" />
                  </div>
                </div>
                <div className="h-6 w-16 rounded-full bg-slate-100" />
                <div className="h-3 w-28 rounded bg-slate-100" />
                <div className="h-3 w-20 rounded bg-slate-100" />
              </div>
            ))
          ) : (
            filteredResources.map((resource) => {
              const styles = typeStyles[resource.type];
              const sharedLabel =
                resource.sharedWith.length > 1
                  ? `${resource.sharedWith[0]} +${resource.sharedWith.length - 1} more`
                  : resource.sharedWith[0];
              return (
                <div
                  key={resource.id}
                  className="grid grid-cols-[minmax(240px,_2fr)_120px_minmax(180px,_1.5fr)_120px] items-center gap-4 px-4 py-4 text-sm text-slate-700"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${styles.icon}`}
                    >
                      <ResourceIcon type={resource.type} />
                    </div>
                    <div className="line-clamp-1 min-w-0">
                      {resource.type === 'Link' ? (
                        <p className="truncate font-semibold text-slate-900" title={resource.name}>
                          {resource.name}
                        </p>
                      ) : (
                        <button
                          type="button"
                          className="truncate text-left font-semibold text-slate-900 hover:underline"
                          onClick={() => handleResourceOpen(resource)}
                          title={resource.name}
                        >
                          {resource.name}
                        </button>
                      )}
                    </div>
                  </div>
                  <span
                    className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${styles.badge}`}
                  >
                    {resource.type}
                  </span>
                  <span className="text-slate-600">{sharedLabel}</span>
                  <span className="text-slate-500">{resource.addedOn}</span>
                </div>
              );
            })
          )}
          {!data.isLoading && filteredResources.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">
              No resources found.
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default ResourcesTable;
